import React, { useState, useEffect } from 'react';
import { 
  QrCode,
  Settings,
  BarChart3,
  Receipt
} from 'lucide-react';

interface Payment {
  id: string;
  orderId: string;
  customerId: string;
  customerName: string;
  amount: number;
  currency: string;
  method: 'paytm' | 'phonepe' | 'gpay' | 'qr_code' | 'credit_card' | 'debit_card' | 'netbanking' | 'cash' | 'upi';
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  createdAt: string;
  completedAt?: string;
  transactionId?: string;
  notes?: string;
  qrCode?: string;
  upiId?: string;
}

interface PaymentMethod {
  id: string;
  name: string;
  type: 'paytm' | 'phonepe' | 'gpay' | 'qr_code' | 'credit_card' | 'debit_card' | 'netbanking' | 'cash' | 'upi';
  isActive: boolean;
  icon: string;
  description: string;
  processingFee: number;
  settlementTime: string;
}

interface PaymentReport {
  period: string;
  totalAmount: number;
  totalTransactions: number;
  successRate: number;
  averageAmount: number;
  topMethod: string;
}

const PaymentIntegration: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'payments' | 'methods' | 'reports' | 'qr-generator'>('payments');
  const [payments, setPayments] = useState<Payment[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [reports, setReports] = useState<PaymentReport[]>([]);
  const [showQRCode, setShowQRCode] = useState(false);
  const [qrAmount, setQrAmount] = useState('');
  const [qrDescription, setQrDescription] = useState('');

  useEffect(() => {
    // TODO: Load real payment data from API
    setPayments([]);
    setPaymentMethods([]);
    setReports([]);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'failed': return 'text-red-600 bg-red-100';
      case 'refunded': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getMethodIcon = (method: string) => {
    switch (method) {
      case 'paytm': return '📱';
      case 'phonepe': return '📱';
      case 'gpay': return '📱';
      case 'qr_code': return '📱';
      case 'credit_card': return '💳';
      case 'debit_card': return '💳';
      case 'netbanking': return '🏦';
      case 'cash': return '💵';
      case 'upi': return '📱';
      default: return '💰';
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handlePaymentStatusUpdate = (paymentId: string, newStatus: Payment['status']) => {
    setPayments(prev => prev.map(payment => 
      payment.id === paymentId 
        ? { ...payment, status: newStatus }
        : payment
    ));
  };

  const handleRefund = (paymentId: string) => {
    setPayments(prev => prev.map(payment => 
      payment.id === paymentId 
        ? { ...payment, status: 'refunded' as Payment['status'] }
        : payment
    ));
  };

  const generateQRCode = () => {
    if (!qrAmount || !qrDescription) {
      alert('Please enter amount and description');
      return;
    }
    setShowQRCode(true);
  };

  const downloadQRCode = () => {
    // Simulate QR code download
    alert('QR Code downloaded successfully!');
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Payment Integration</h1>
          <p className="text-gray-600">Manage payments, methods, and reports for Indian market</p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <nav className="flex space-x-8 border-b border-gray-200">
            {[
              { id: 'payments', label: 'Payments', icon: Receipt },
              { id: 'methods', label: 'Payment Methods', icon: Settings },
              { id: 'reports', label: 'Reports', icon: BarChart3 },
              { id: 'qr-generator', label: 'QR Generator', icon: QrCode }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow">
          {activeTab === 'payments' && (
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Recent Payments</h2>
                <div className="flex space-x-2">
                  <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                    Export
                  </button>
                  <button className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">
                    Refresh
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Payment
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Method
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {payments.map((payment) => (
                      <tr key={payment.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {payment.customerName}
                            </div>
                            <div className="text-sm text-gray-500">
                              Order: {payment.orderId}
                            </div>
                            {payment.transactionId && (
                              <div className="text-xs text-gray-400">
                                TXN: {payment.transactionId}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className="text-lg mr-2">{getMethodIcon(payment.method)}</span>
                            <span className="text-sm font-medium text-gray-900 capitalize">
                              {payment.method.replace('_', ' ')}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {formatCurrency(payment.amount, payment.currency)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(payment.status)}`}>
                            {payment.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(payment.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            {payment.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => handlePaymentStatusUpdate(payment.id, 'completed')}
                                  className="text-green-600 hover:text-green-900"
                                >
                                  Complete
                                </button>
                                <button
                                  onClick={() => handlePaymentStatusUpdate(payment.id, 'failed')}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  Fail
                                </button>
                              </>
                            )}
                            {payment.status === 'completed' && (
                              <button
                                onClick={() => handleRefund(payment.id)}
                                className="text-blue-600 hover:text-blue-900"
                              >
                                Refund
                              </button>
                            )}
                            {payment.qrCode && (
                              <button className="text-purple-600 hover:text-purple-900">
                                View QR
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'methods' && (
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Payment Methods</h2>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  Add Method
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {paymentMethods.map((method) => (
                  <div key={method.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <span className="text-2xl mr-3">{method.icon}</span>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{method.name}</h3>
                          <p className="text-sm text-gray-500">{method.description}</p>
                        </div>
                      </div>
                      <div className={`w-3 h-3 rounded-full ${method.isActive ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Processing Fee:</span>
                        <span className="font-medium">{method.processingFee}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Settlement:</span>
                        <span className="font-medium">{method.settlementTime}</span>
                      </div>
                    </div>

                    <div className="mt-4 flex space-x-2">
                      <button className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">
                        Edit
                      </button>
                      <button className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">
                        {method.isActive ? 'Disable' : 'Enable'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'reports' && (
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Payment Reports</h2>
                <div className="flex space-x-2">
                  <select className="px-3 py-2 border border-gray-300 rounded-md">
                    <option>Last 7 days</option>
                    <option>Last 30 days</option>
                    <option>Last 90 days</option>
                  </select>
                  <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                    Export Report
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {reports.map((report) => (
                  <div key={report.period} className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">{report.period}</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Total Amount:</span>
                        <span className="font-semibold">{formatCurrency(report.totalAmount, 'INR')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Transactions:</span>
                        <span className="font-semibold">{report.totalTransactions}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Success Rate:</span>
                        <span className="font-semibold text-green-600">{report.successRate}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Avg Amount:</span>
                        <span className="font-semibold">{formatCurrency(report.averageAmount, 'INR')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Top Method:</span>
                        <span className="font-semibold">{report.topMethod}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Analytics</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600 mb-2">₹1,85,000</div>
                    <div className="text-sm text-gray-500">Total Revenue</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600 mb-2">95</div>
                    <div className="text-sm text-gray-500">Successful Payments</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-purple-600 mb-2">89.5%</div>
                    <div className="text-sm text-gray-500">Success Rate</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'qr-generator' && (
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">QR Code Generator</h2>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  Generate QR
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Amount (₹)
                    </label>
                    <input
                      type="number"
                      value={qrAmount}
                      onChange={(e) => setQrAmount(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter amount"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <input
                      type="text"
                      value={qrDescription}
                      onChange={(e) => setQrDescription(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Payment description"
                    />
                  </div>

                  <button
                    onClick={generateQRCode}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Generate QR Code
                  </button>
                </div>

                <div className="flex flex-col items-center justify-center">
                  {showQRCode ? (
                    <div className="text-center">
                      <div className="w-64 h-64 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center mb-4">
                        <div className="text-center">
                          <div className="text-6xl mb-2">📱</div>
                          <div className="text-sm text-gray-500">QR Code Generated</div>
                          <div className="text-lg font-semibold mt-2">{formatCurrency(parseFloat(qrAmount), 'INR')}</div>
                          <div className="text-sm text-gray-500">{qrDescription}</div>
                        </div>
                      </div>
                      <button
                        onClick={downloadQRCode}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                      >
                        Download QR Code
                      </button>
                    </div>
                  ) : (
                    <div className="w-64 h-64 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                      <div className="text-center text-gray-500">
                        <QrCode className="w-16 h-16 mx-auto mb-2" />
                        <div>QR Code will appear here</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentIntegration; 